import { CONFIG, TileType, DIFFICULTIES, DifficultySettings } from '../config';
import { SeededRandom } from '../utils/SeededRandom';
import { TileMap } from './TileMap';
import { SolvabilityChecker, SolvabilityResult } from './SolvabilityChecker';

export interface GeneratedLevel {
  map: TileMap;
  enemyAssistedGoldPositions: { x: number; y: number }[];
}

export class LevelGenerator {
  private rng: SeededRandom;
  private difficulty: DifficultySettings;
  private checker: SolvabilityChecker;
  private levelNumber: number;
  private difficultyKey: string;
  
  // Last generation result for tracking enemy-assisted gold
  private lastSolvabilityResult: SolvabilityResult | null = null;
  
  constructor(seed: string | number, difficultyKey: string = 'normal', levelNumber: number = 1) {
    this.rng = new SeededRandom(seed);
    this.difficulty = DIFFICULTIES[difficultyKey] || DIFFICULTIES.normal;
    this.checker = new SolvabilityChecker();
    this.checker.setDifficulty(difficultyKey);
    this.levelNumber = levelNumber;
    this.difficultyKey = difficultyKey;
  }
  
  /**
   * Get the positions of enemy-assisted gold from the last generation.
   */
  getEnemyAssistedGoldPositions(): { x: number; y: number }[] {
    return this.lastSolvabilityResult?.enemyAssistedPositions || [];
  }
  
  /**
   * Generate a level with full information including enemy-assisted gold.
   */
  generateWithInfo(maxAttempts = 500): GeneratedLevel {
    const map = this.generate(maxAttempts);
    // Use the solvability checker's analysis which is more accurate than
    // the generator's quick reachability estimate. The checker does thorough
    // BFS including digging possibilities.
    const enemyAssistedPositions = this.lastSolvabilityResult?.enemyAssistedPositions || [];
    
    // Update the map's tracking to match the authoritative checker result
    map.enemyAssistedGoldPositions = enemyAssistedPositions;
    
    return {
      map,
      enemyAssistedGoldPositions: enemyAssistedPositions
    };
  }
  
  generate(maxAttempts = 500): TileMap {
    let bestMap: TileMap | null = null;
    let bestScore = -1;
    let bestResult: SolvabilityResult | null = null;
    
    // Phase 1: Try to generate a fully solvable level
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const map = this.generateCandidate();
      const result = this.checker.checkSolvability(map);
      const { solvable, score, debug } = result;
      
      if (solvable) {
        console.log(`Generated solvable level on attempt ${attempt + 1}`);
        if (result.enemyAssistedGold > 0) {
          console.log(`  -> ${result.enemyAssistedGold} enemy-assisted gold piece(s)`);
        }
        this.lastSolvabilityResult = result;
        return map;
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMap = map;
        bestResult = result;
      }
      
      if (attempt < 10) {
        console.log(`Attempt ${attempt + 1}: score=${score}, ${debug}`);
      }
    }
    
    // Phase 2: If we have a near-perfect map (all gold reachable), use it
    if (bestMap && bestScore >= 0.99) {
      console.log(`Using best candidate with score ${bestScore}`);
      this.lastSolvabilityResult = bestResult;
      return bestMap;
    }
    
    // Phase 3: Try additional attempts with the seed modified
    // This gives us more variety in generation
    console.log(`Phase 2: Trying modified seeds (best so far: ${bestScore})`);
    for (let retry = 0; retry < 200; retry++) {
      // Modify the RNG state by advancing it
      this.rng.next();
      this.rng.next();
      
      const map = this.generateCandidate();
      const result = this.checker.checkSolvability(map);
      const { solvable, score, debug } = result;
      
      if (solvable) {
        console.log(`Generated solvable level on retry ${retry + 1}`);
        if (result.enemyAssistedGold > 0) {
          console.log(`  -> ${result.enemyAssistedGold} enemy-assisted gold piece(s)`);
        }
        this.lastSolvabilityResult = result;
        return map;
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMap = map;
        bestResult = result;
      }
    }
    
    // Phase 4: Accept best map if it's reasonably good (95%+ gold reachable with exit)
    if (bestMap && bestScore >= 0.95) {
      console.log(`Using best candidate with score ${bestScore} (95%+ threshold)`);
      this.lastSolvabilityResult = bestResult;
      return bestMap;
    }
    
    // Last resort fallback - should be very rare
    console.warn(`Failed to generate solvable level after 700 attempts, using fallback (best score: ${bestScore})`);
    this.lastSolvabilityResult = null;  // Fallback has no enemy-assisted gold
    return this.generateFallback();
  }
  
  private generateCandidate(): TileMap {
    const map = new TileMap();
    
    // Step 1: Create ground floor
    this.createGround(map);
    
    // Step 2: Create a main vertical "spine" ladder first
    this.createMainSpine(map);
    
    // Step 3: Create platforms with guaranteed connectivity
    this.createConnectedPlatforms(map);
    
    // Step 4: Add additional ladders
    this.addExtraLadders(map);
    
    // Step 5: Add poles/bars
    this.createPoles(map);
    
    // Step 6: Add some hard bricks (but not blocking ladders)
    this.addHardBricks(map);
    
    // Step 7: Convert some bricks to trap bricks
    this.addTrapBricks(map);
    
    // Step 8: Ensure ladders are accessible (clear blocking bricks)
    this.ensureLadderAccessibility(map);
    
    // Step 9: Place player start (bottom area)
    this.placePlayer(map);
    
    // Step 10: Place gold (only in reachable spots)
    this.placeGold(map);
    
    // Step 11: Place enemies
    this.placeEnemies(map);
    
    // Step 12: Add exit ladders at top (using existing ladders)
    this.addExitLadders(map);
    
    return map;
  }
  
  private createMainSpine(map: TileMap): void {
    // Create a main ladder that goes from bottom to near-top
    // This guarantees basic vertical connectivity
    // For harder difficulties, make the spine less direct (zigzag)
    const complexity = this.difficulty.complexity ?? 0.6;
    let spineX = this.rng.range(Math.floor(map.width * 0.25), Math.floor(map.width * 0.75));
    
    // Decide if spine should zigzag (more likely at high complexity)
    const zigzag = complexity > 0.5 && this.rng.next() < complexity;
    const zigzagInterval = zigzag ? this.rng.range(3, 5) : 999;
    
    // Start spine from row 4 (leave top rows for exit ladder only)
    const spineStartY = 4;
    for (let y = spineStartY; y < map.height - 1; y++) {
      map.setTile(spineX, y, TileType.LADDER);
      
      // Zigzag: shift spine left or right periodically
      if (zigzag && y > 0 && y % zigzagInterval === 0 && y < map.height - 3) {
        const shift = this.rng.range(2, 5) * (this.rng.next() < 0.5 ? -1 : 1);
        const newX = Math.max(2, Math.min(map.width - 3, spineX + shift));
        
        // Create horizontal connection
        const startX = Math.min(spineX, newX);
        const endX = Math.max(spineX, newX);
        for (let x = startX; x <= endX; x++) {
          if (map.getTile(x, y) === TileType.EMPTY) {
            map.setTile(x, y, TileType.LADDER);
          }
        }
        spineX = newX;
      }
    }
  }
  
  private createGround(map: TileMap): void {
    // Bottom row is mostly solid
    const y = map.height - 1;
    for (let x = 0; x < map.width; x++) {
      map.setTile(x, y, TileType.BRICK);
    }
  }
  
  private createConnectedPlatforms(map: TileMap): void {
    // Create platform rows with ladders connecting them
    // More complex levels have more platform rows to navigate
    const platformRows = [map.height - 4, map.height - 7, map.height - 10, 3, map.height - 13];
    
    // Complexity controls how many platform layers we have
    // complexity 0.4 = 2 platforms, 0.6 = 3, 0.8 = 4, 1.0 = 5
    const complexity = this.difficulty.complexity ?? 0.6;
    const numPlatforms = Math.floor(1 + complexity * 4);
    const activePlatforms = platformRows.slice(0, Math.min(numPlatforms, platformRows.length));
    
    let previousLadderPositions: number[] = [];
    
    for (let i = 0; i < activePlatforms.length; i++) {
      const row = activePlatforms[i];
      const ladderPositions = this.createPlatformWithLadders(map, row, previousLadderPositions);
      previousLadderPositions = ladderPositions;
    }
  }
  
  private createPlatformWithLadders(map: TileMap, row: number, connectFrom: number[]): number[] {
    const ladderPositions: number[] = [];
    
    // Create platform segments - more complex levels have more segments
    const complexity = this.difficulty.complexity ?? 0.6;
    const minSegments = Math.floor(2 + complexity);
    const maxSegments = Math.floor(3 + complexity * 3);
    const numSegments = this.rng.range(minSegments, maxSegments + 1);
    const segmentWidth = Math.floor(map.width / numSegments);
    
    for (let seg = 0; seg < numSegments; seg++) {
      const startX = seg * segmentWidth + this.rng.range(0, 3);
      const endX = Math.min(startX + this.rng.range(4, segmentWidth), map.width - 1);
      
      // Leave gap between segments
      if (seg > 0 && startX < seg * segmentWidth + 2) continue;
      
      // Create platform
      for (let x = startX; x <= endX; x++) {
        map.setTile(x, row, TileType.BRICK);
      }
      
      // Add ladder somewhere in this segment - probability based on ladder density
      if (this.rng.next() < this.difficulty.ladderDensity) {
        const ladderX = startX + this.rng.range(1, Math.max(2, endX - startX - 1));
        if (ladderX >= startX && ladderX <= endX) {
          this.extendLadderDown(map, ladderX, row);
          ladderPositions.push(ladderX);
        }
      }
    }
    
    // Ensure at least one ladder connects to a previous ladder position (for solvability)
    if (connectFrom.length > 0) {
      const targetX = this.rng.pick(connectFrom);
      // Find nearest platform position
      for (let dx = 0; dx < 5; dx++) {
        for (const offset of [dx, -dx]) {
          const x = targetX + offset;
          if (x >= 0 && x < map.width && map.getTile(x, row) === TileType.BRICK) {
            this.extendLadderDown(map, x, row);
            if (!ladderPositions.includes(x)) {
              ladderPositions.push(x);
            }
            break;
          }
        }
      }
    }
    
    return ladderPositions;
  }
  
  private extendLadderDown(map: TileMap, x: number, fromRow: number): void {
    // Extend ladder downward to next solid surface
    // The ladder should reach the platform surface (so player can step off)
    for (let y = fromRow; y < map.height; y++) {
      const tile = map.getTile(x, y);
      // Stop when we hit a hard brick or the bottom
      if (tile === TileType.BRICK_HARD) {
        break;
      }
      // If this is a regular brick, replace it with ladder (creating access point)
      // but stop - don't go below solid ground
      if (tile === TileType.BRICK || tile === TileType.BRICK_TRAP) {
        // Don't dig into the ground floor
        if (y < map.height - 1) {
          map.setTile(x, y, TileType.LADDER);
        }
        break;
      }
      map.setTile(x, y, TileType.LADDER);
    }
  }
  
  private addExtraLadders(map: TileMap): void {
    // Add some random ladders for more paths
    // Ladder density controls how many extra easy routes exist
    const extraLadders = Math.floor(this.difficulty.ladderDensity * 10);
    
    for (let i = 0; i < extraLadders; i++) {
      const x = this.rng.range(2, map.width - 2);
      
      // Find a platform
      for (let y = 2; y < map.height - 2; y++) {
        if (map.getTile(x, y) === TileType.BRICK && map.getTile(x, y - 1) === TileType.EMPTY) {
          this.extendLadderDown(map, x, y - 1);
          break;
        }
      }
    }
  }
  
  private createPoles(map: TileMap): void {
    // Poles in classic Lode Runner serve specific purposes:
    // 1. Bridge gaps between platforms (ONLY when they connect two reachable areas)
    // 2. Provide access to isolated areas that would otherwise be unreachable
    // 3. Create strategic routes for escaping enemies
    // 
    // Poles should NEVER:
    // - Hang over empty space with no purpose
    // - Be placed directly above solid floor where walking would suffice
    // - Lead to dead ends
    
    // First, identify all platform segments and their edges
    interface Platform {
      y: number;        // Row (surface level - the row you walk on is y+1)
      startX: number;
      endX: number;
    }
    
    const platforms: Platform[] = [];
    
    for (let y = 1; y < map.height - 1; y++) {
      let platformStart = -1;
      for (let x = 0; x < map.width; x++) {
        const isWalkable = this.isWalkableSurface(map, x, y);
        
        if (isWalkable && platformStart === -1) {
          platformStart = x;
        } else if (!isWalkable && platformStart !== -1) {
          platforms.push({ y: y - 1, startX: platformStart, endX: x - 1 });
          platformStart = -1;
        }
      }
      if (platformStart !== -1) {
        platforms.push({ y: y - 1, startX: platformStart, endX: map.width - 1 });
      }
    }
    
    // Find pairs of platforms at the same height that could be connected
    const polesCreated: { y: number; startX: number; endX: number }[] = [];
    
    for (let i = 0; i < platforms.length; i++) {
      for (let j = i + 1; j < platforms.length; j++) {
        const p1 = platforms[i];
        const p2 = platforms[j];
        
        // Must be at same height
        if (p1.y !== p2.y) continue;
        
        // Determine gap between them
        const leftPlatform = p1.endX < p2.startX ? p1 : p2;
        const rightPlatform = p1.endX < p2.startX ? p2 : p1;
        
        const gapStart = leftPlatform.endX + 1;
        const gapEnd = rightPlatform.startX - 1;
        const gapLength = gapEnd - gapStart + 1;
        
        // Gap must be meaningful (3-15 tiles) - walking over small gaps defeats the purpose
        if (gapLength < 3 || gapLength > 15) continue;
        
        // Check if path is clear (no obstructions)
        let pathClear = true;
        for (let x = gapStart; x <= gapEnd; x++) {
          const tile = map.getTile(x, p1.y);
          if (tile !== TileType.EMPTY && tile !== TileType.GOLD) {
            pathClear = false;
            break;
          }
        }
        if (!pathClear) continue;
        
        // IMPORTANT: Don't create pole if there's solid ground directly below the entire gap
        // (that would be pointless - you could just walk)
        let hasDropZone = false;
        for (let x = gapStart; x <= gapEnd; x++) {
          const below = map.getTile(x, p1.y + 1);
          if (below !== TileType.BRICK && below !== TileType.BRICK_HARD && below !== TileType.BRICK_TRAP) {
            hasDropZone = true;
            break;
          }
        }
        if (!hasDropZone) continue;
        
        // Don't overlap with existing poles
        let overlaps = false;
        for (const existing of polesCreated) {
          if (existing.y === p1.y && 
              !(gapEnd < existing.startX || gapStart > existing.endX)) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) continue;
        
        // Create pole with some randomness (not all valid gaps get poles)
        const connectionChance = 0.5 + this.difficulty.complexity * 0.3;
        if (this.rng.chance(connectionChance)) {
          // Create pole from platform edge to platform edge
          for (let x = leftPlatform.endX; x <= rightPlatform.startX; x++) {
            const current = map.getTile(x, p1.y);
            if (current === TileType.EMPTY) {
              map.setTile(x, p1.y, TileType.POLE);
            }
          }
          polesCreated.push({ y: p1.y, startX: leftPlatform.endX, endX: rightPlatform.startX });
        }
      }
    }
    
    // Secondary: Create poles that extend from ladders to reach isolated platforms
    // This is for cases where a ladder is near a platform but doesn't quite connect
    for (let y = 2; y < map.height - 3; y++) {
      for (let x = 2; x < map.width - 2; x++) {
        if (map.getTile(x, y) !== TileType.LADDER) continue;
        
        // Check each direction for a nearby platform that's not directly reachable
        for (const dir of [-1, 1]) {
          // Look for a platform edge in this direction
          let targetX = -1;
          for (let dx = 1; dx <= 8; dx++) {
            const checkX = x + dir * dx;
            if (checkX < 0 || checkX >= map.width) break;
            
            const tile = map.getTile(checkX, y);
            if (tile !== TileType.EMPTY && tile !== TileType.POLE) break;
            
            // Check if there's a platform to land on here
            const below = map.getTile(checkX, y + 1);
            if (below === TileType.BRICK || below === TileType.BRICK_HARD || below === TileType.LADDER) {
              targetX = checkX;
              break;
            }
          }
          
          // Only create pole if target is at least 3 tiles away and we found a landing spot
          if (targetX !== -1 && Math.abs(targetX - x) >= 3) {
            // Check there's no floor directly connecting these positions
            let needsPole = false;
            for (let checkX = Math.min(x, targetX); checkX <= Math.max(x, targetX); checkX++) {
              const below = map.getTile(checkX, y + 1);
              if (below !== TileType.BRICK && below !== TileType.BRICK_HARD && below !== TileType.LADDER) {
                needsPole = true;
                break;
              }
            }
            
            if (needsPole && this.rng.chance(0.35)) {
              const startX = Math.min(x, targetX);
              const endX = Math.max(x, targetX);
              for (let fillX = startX; fillX <= endX; fillX++) {
                const current = map.getTile(fillX, y);
                if (current === TileType.EMPTY) {
                  map.setTile(fillX, y, TileType.POLE);
                }
              }
            }
          }
        }
      }
    }
  }
  
  /**
   * Check if a position has a walkable surface (player can stand on the tile below)
   */
  private isWalkableSurface(map: TileMap, x: number, y: number): boolean {
    if (y >= map.height) return false;
    const tile = map.getTile(x, y);
    return tile === TileType.BRICK || 
           tile === TileType.BRICK_HARD || 
           tile === TileType.BRICK_TRAP ||
           tile === TileType.LADDER;
  }
  
  private addHardBricks(map: TileMap): void {
    // Scatter some hard bricks
    for (let y = 1; y < map.height - 1; y++) {
      for (let x = 0; x < map.width; x++) {
        if (map.getTile(x, y) === TileType.BRICK && this.rng.chance(0.05)) {
          map.setTile(x, y, TileType.BRICK_HARD);
        }
      }
    }
  }
  
  private addTrapBricks(map: TileMap): void {
    const chance = this.difficulty.trapBrickChance;
    for (let y = 1; y < map.height - 1; y++) {
      for (let x = 0; x < map.width; x++) {
        if (map.getTile(x, y) === TileType.BRICK && this.rng.chance(chance)) {
          // Don't trap brick if it's directly above a ladder
          if (map.getTile(x, y + 1) === TileType.LADDER) continue;
          map.setTile(x, y, TileType.BRICK_TRAP);
        }
      }
    }
  }
  
  /**
   * Ensure ladders have accessible entry/exit points.
   * Remove bricks that block ladder tops.
   */
  private ensureLadderAccessibility(map: TileMap): void {
    for (let x = 0; x < map.width; x++) {
      for (let y = 1; y < map.height - 1; y++) {
        const tile = map.getTile(x, y);
        
        // If there's a ladder here
        if (tile === TileType.LADDER || tile === TileType.LADDER_EXIT) {
          // Check if there's a brick directly above that would block it
          const above = map.getTile(x, y - 1);
          if (above === TileType.BRICK || above === TileType.BRICK_TRAP) {
            // Check if there's a way to reach the ladder from the side
            const leftClear = x > 0 && !map.isSolid(x - 1, y);
            const rightClear = x < map.width - 1 && !map.isSolid(x + 1, y);
            
            // If no side access and ladder continues up, we need platform access
            if (!leftClear && !rightClear) {
              // Remove the blocking brick and make it a platform edge
              map.setTile(x, y - 1, TileType.EMPTY);
            }
          }
          
          // Ensure ladder top has an accessible entry point
          // Find the top of this ladder
          let ladderTop = y;
          while (ladderTop > 0 && (map.getTile(x, ladderTop - 1) === TileType.LADDER || map.getTile(x, ladderTop - 1) === TileType.LADDER_EXIT)) {
            ladderTop--;
          }
          
          // If at the very top of map, it's an exit - ok
          if (ladderTop === 0) continue;
          
          // Otherwise, ensure there's a platform to step onto at ladder top
          // The tile above ladder top should be empty, and there should be
          // at least one side accessible
          const aboveTop = map.getTile(x, ladderTop - 1);
          if (aboveTop === TileType.BRICK || aboveTop === TileType.BRICK_HARD || aboveTop === TileType.BRICK_TRAP) {
            // Ladder is blocked! Check if we can access from sides at ladderTop
            const leftAccessible = x > 0 && !map.isSolid(x - 1, ladderTop) && map.isSupport(x - 1, ladderTop + 1);
            const rightAccessible = x < map.width - 1 && !map.isSolid(x + 1, ladderTop) && map.isSupport(x + 1, ladderTop + 1);
            
            if (!leftAccessible && !rightAccessible) {
              // No side access, need to clear the blocking brick
              map.setTile(x, ladderTop - 1, TileType.EMPTY);
            }
          }
        }
      }
    }
  }
  
  private placePlayer(map: TileMap): void {
    // Place player on ground floor
    const groundY = map.height - 2;
    
    // Find valid spot on ground
    for (let attempts = 0; attempts < 20; attempts++) {
      const x = this.rng.range(2, map.width - 2);
      if (map.getTile(x, groundY) === TileType.EMPTY || 
          map.getTile(x, groundY) === TileType.LADDER) {
        map.playerStart = { x, y: groundY };
        return;
      }
    }
    
    // Fallback - find any empty spot on ground
    for (let x = 1; x < map.width - 1; x++) {
      if (!map.isSolid(x, groundY)) {
        map.playerStart = { x, y: groundY };
        return;
      }
    }
    
    map.playerStart = { x: 2, y: groundY };
  }
  
  private placeGold(map: TileMap): void {
    const [minGold, maxGold] = this.difficulty.gold;
    const goldCount = this.rng.range(minGold, maxGold + 1);
    
    // First, compute reachable positions from player start using BFS
    const playerReachable = this.computeReachablePositions(map);
    
    // For Hard/Ninja, also compute enemy-reachable positions for potential enemy-assisted gold
    const maxEnemyAssisted = this.getMaxEnemyAssistedGold();
    let enemyReachable: Set<string> | null = null;
    
    if (maxEnemyAssisted > 0) {
      // Compute enemy spawn and reachable positions
      const enemySpawns = this.computeEnemySpawnPositions(map);
      enemyReachable = this.computeEnemyReachablePositions(map, enemySpawns);
    }
    
    // Collect valid gold spots in categories by vertical zone:
    // - Upper zone (rows 1-5): priority for interesting placement
    // - Middle zone (rows 6-10): good placement
    // - Lower zone (rows 11-13): limited placement
    // - Ground floor (row 14 = height-2): very limited
    const groundY = map.height - 2;
    
    const upperSpots: { x: number; y: number }[] = [];    // rows 1-5
    const middleSpots: { x: number; y: number }[] = [];   // rows 6-10  
    const lowerSpots: { x: number; y: number }[] = [];    // rows 11-13
    const groundSpots: { x: number; y: number }[] = [];   // row 14 (ground)
    const enemyOnlySpots: { x: number; y: number }[] = [];
    
    for (let y = 1; y < map.height - 1; y++) {
      for (let x = 0; x < map.width; x++) {
        if (!this.isValidGoldSpot(map, x, y)) continue;
        
        const key = `${x},${y}`;
        if (playerReachable.has(key)) {
          // Categorize by vertical zone
          if (y <= 5) {
            upperSpots.push({ x, y });
          } else if (y <= 10) {
            middleSpots.push({ x, y });
          } else if (y < groundY) {
            lowerSpots.push({ x, y });
          } else {
            groundSpots.push({ x, y });
          }
        } else if (enemyReachable && enemyReachable.has(key)) {
          enemyOnlySpots.push({ x, y });
        }
      }
    }
    
    // Shuffle each zone
    this.rng.shuffle(upperSpots);
    this.rng.shuffle(middleSpots);
    this.rng.shuffle(lowerSpots);
    this.rng.shuffle(groundSpots);
    this.rng.shuffle(enemyOnlySpots);
    
    // Combine spots with priority: upper > middle > lower > ground
    // This ensures vertical distribution
    const playerReachableSpots = [
      ...upperSpots,
      ...middleSpots, 
      ...lowerSpots,
      ...groundSpots
    ];
    
    // For Hard/Ninja debug purposes only (uncomment to debug):
    // if (maxEnemyAssisted > 0) {
    //   console.log(`Gold spots: ${playerReachableSpots.length} player-reachable, ${enemyOnlySpots.length} enemy-only`);
    // }
    
    // Place gold in two phases:
    // 1. Player-reachable gold (the majority)
    // 2. For Hard/Ninja: potentially some enemy-assisted gold
    const placed: { x: number; y: number }[] = [];
    
    // For Hard/Ninja: decide FIRST if we want enemy-assisted gold
    // This way we can reserve slots for it
    let numEnemyAssisted = 0;
    if (maxEnemyAssisted > 0 && enemyOnlySpots.length > 0) {
      // Hard: ~40% chance of 1 enemy-assisted gold
      // Ninja: ~60% chance, can have 1-2
      const enemyAssistedChance = this.difficultyKey === 'ninja' ? 0.6 : 0.4;
      
      if (this.rng.next() < enemyAssistedChance) {
        numEnemyAssisted = Math.min(
          maxEnemyAssisted,
          enemyOnlySpots.length,
          this.difficultyKey === 'ninja' ? this.rng.range(1, 3) : 1
        );
        // console.log(`Planning ${numEnemyAssisted} enemy-assisted gold piece(s)`);
      }
    }
    
    // Player-reachable gold target (total minus enemy-assisted)
    const playerGoldTarget = goldCount - numEnemyAssisted;
    
    // Track gold per vertical zone to ensure distribution
    let groundGoldCount = 0;
    const maxGroundGold = Math.max(2, Math.floor(goldCount / 5)); // Max ~20% on ground
    
    // Phase 1: Place player-reachable gold with spacing
    for (const spot of playerReachableSpots) {
      if (placed.length >= playerGoldTarget) break;
      
      // Limit ground floor gold
      if (spot.y >= groundY && groundGoldCount >= maxGroundGold) continue;
      
      // Check distance from other gold - require more horizontal spread
      // and some vertical separation
      const tooClose = placed.some(p => {
        const dx = Math.abs(p.x - spot.x);
        const dy = Math.abs(p.y - spot.y);
        // Require at least 3 tiles horizontal OR 2 tiles vertical separation
        return dx < 3 && dy < 2;
      });
      
      if (!tooClose) {
        map.setTile(spot.x, spot.y, TileType.GOLD);
        map.goldPositions.push(spot);
        placed.push(spot);
        if (spot.y >= groundY) groundGoldCount++;
      }
    }
    
    // Fill remaining player gold with spacing-relaxed (but still limit ground)
    for (const spot of playerReachableSpots) {
      if (placed.length >= playerGoldTarget) break;
      if (placed.some(p => p.x === spot.x && p.y === spot.y)) continue;
      if (spot.y >= groundY && groundGoldCount >= maxGroundGold) continue;
      
      map.setTile(spot.x, spot.y, TileType.GOLD);
      map.goldPositions.push(spot);
      placed.push(spot);
      if (spot.y >= groundY) groundGoldCount++;
    }
    
    // Phase 2: Place enemy-assisted gold
    let enemyAssistedPlaced = 0;
    for (let i = 0; i < numEnemyAssisted && i < enemyOnlySpots.length; i++) {
      const spot = enemyOnlySpots[i];
      
      // Check distance from other gold (slightly relaxed)
      const tooClose = placed.some(p => 
        Math.abs(p.x - spot.x) + Math.abs(p.y - spot.y) < 3
      );
      
      if (!tooClose) {
        map.setTile(spot.x, spot.y, TileType.GOLD);
        map.goldPositions.push(spot);
        placed.push(spot);
        enemyAssistedPlaced++;
      }
    }
    
    // Note: We don't track enemy-assisted gold here because this placement
    // uses a simplified reachability check. The SolvabilityChecker's analysis
    // is authoritative and will determine actual enemy-assisted positions later.
  }
  
  /**
   * Get max allowed enemy-assisted gold for current difficulty
   */
  private getMaxEnemyAssistedGold(): number {
    switch (this.difficultyKey) {
      case 'hard':
        return 1;  // Allow max 1 enemy-assisted gold
      case 'ninja':
        return 2;  // Allow max 1-2 enemy-assisted gold
      default:
        return 0;  // Easy/Normal: no enemy-assisted gold
    }
  }
  
  /**
   * Compute all positions where enemies can spawn/respawn.
   */
  private computeEnemySpawnPositions(map: TileMap): { x: number; y: number }[] {
    const spawns: { x: number; y: number }[] = [];
    
    // Primary spawns: top rows on ladders or empty space
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < map.width; x++) {
        const tile = map.getTile(x, y);
        if (tile === TileType.LADDER || tile === TileType.LADDER_EXIT ||
            tile === TileType.EMPTY || tile === TileType.POLE) {
          spawns.push({ x, y });
        }
      }
    }
    
    // Also include initial enemy start positions
    for (const start of map.enemyStarts) {
      if (!spawns.some(s => s.x === start.x && s.y === start.y)) {
        spawns.push(start);
      }
    }
    
    return spawns;
  }
  
  /**
   * Compute all positions reachable by enemies from spawn positions.
   */
  private computeEnemyReachablePositions(map: TileMap, spawns: { x: number; y: number }[]): Set<string> {
    const visited = new Set<string>();
    const queue: { x: number; y: number }[] = [];
    
    for (const spawn of spawns) {
      const key = `${spawn.x},${spawn.y}`;
      if (!visited.has(key)) {
        visited.add(key);
        queue.push(spawn);
      }
    }
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      const neighbors = this.getEnemyReachableNeighbors(map, current);
      
      for (const next of neighbors) {
        const key = `${next.x},${next.y}`;
        if (!visited.has(key)) {
          visited.add(key);
          queue.push(next);
        }
      }
    }
    
    return visited;
  }
  
  /**
   * Get positions reachable by enemy from current position (no digging).
   */
  private getEnemyReachableNeighbors(map: TileMap, pos: { x: number; y: number }): { x: number; y: number }[] {
    const neighbors: { x: number; y: number }[] = [];
    const { x, y } = pos;
    
    const onLadder = map.isClimbable(x, y);
    const onBar = map.isBar(x, y);
    const hasSupport = onLadder || onBar || map.isSupport(x, y + 1) || y + 1 >= map.height;
    
    if (hasSupport) {
      // Walk left/right
      for (const dx of [-1, 1]) {
        const nx = x + dx;
        if (nx >= 0 && nx < map.width && !map.isSolid(nx, y)) {
          // Simulate falling
          let ny = y;
          while (ny < map.height - 1) {
            if (map.isClimbable(nx, ny) || map.isBar(nx, ny) || map.isSupport(nx, ny + 1)) break;
            ny++;
          }
          neighbors.push({ x: nx, y: ny });
        }
      }
    }
    
    // Climb up (must be on ladder)
    if (onLadder && y > 0 && !map.isSolid(x, y - 1)) {
      neighbors.push({ x, y: y - 1 });
    }
    
    // Climb down
    if ((onLadder || map.isClimbable(x, y + 1)) && y < map.height - 1 && !map.isSolid(x, y + 1)) {
      neighbors.push({ x, y: y + 1 });
    }
    
    return neighbors;
  }
  
  private isValidGoldSpot(map: TileMap, x: number, y: number): boolean {
    if (map.getTile(x, y) !== TileType.EMPTY) return false;
    
    // Need support below
    const below = map.getTile(x, y + 1);
    const hasSupport = below === TileType.BRICK || 
                       below === TileType.BRICK_HARD || 
                       below === TileType.LADDER ||
                       below === TileType.BRICK_TRAP;
    if (!hasSupport) return false;
    
    // Must be reachable - either:
    // 1. On a ladder
    // 2. Has walkable approach from left or right
    // 3. Can be reached by falling from above
    
    const onLadder = map.getTile(x, y) === TileType.LADDER || below === TileType.LADDER;
    if (onLadder) return true;
    
    // Check if there's a walkable approach from either side
    const leftClear = x > 0 && !map.isSolid(x - 1, y);
    const rightClear = x < map.width - 1 && !map.isSolid(x + 1, y);
    const hasApproach = leftClear || rightClear;
    
    // Check if there's a platform to walk from (support at neighbor position)
    const leftHasSupport = x > 0 && (map.isSupport(x - 1, y + 1) || map.isClimbable(x - 1, y));
    const rightHasSupport = x < map.width - 1 && (map.isSupport(x + 1, y + 1) || map.isClimbable(x + 1, y));
    
    if (hasApproach && (leftHasSupport || rightHasSupport)) return true;
    
    // Check if reachable by falling through from above (via pole or empty space)
    for (let checkY = y - 1; checkY >= 0; checkY--) {
      const tile = map.getTile(x, checkY);
      if (tile === TileType.POLE || tile === TileType.LADDER) {
        return true; // Can drop from pole/ladder above
      }
      if (map.isSolid(x, checkY)) {
        break; // Blocked by solid tile
      }
    }
    
    return false;
  }
  
  /**
   * Compute all positions reachable from player start using BFS.
   * This is a simplified version that doesn't consider exit ladders.
   */
  private computeReachablePositions(map: TileMap): Set<string> {
    const visited = new Set<string>();
    const queue: { x: number; y: number }[] = [map.playerStart];
    visited.add(`${map.playerStart.x},${map.playerStart.y}`);
    
    while (queue.length > 0) {
      const { x, y } = queue.shift()!;
      
      const onLadder = map.isClimbable(x, y);
      const onBar = map.isBar(x, y);
      const hasSupport = onLadder || onBar || map.isSupport(x, y + 1) || y + 1 >= map.height;
      
      // Walk left/right with falling
      if (hasSupport) {
        for (const dx of [-1, 1]) {
          const nx = x + dx;
          if (nx >= 0 && nx < map.width && !map.isSolid(nx, y)) {
            // Simulate falling
            let ny = y;
            while (ny < map.height - 1) {
              if (map.isClimbable(nx, ny) || map.isBar(nx, ny) || map.isSupport(nx, ny + 1)) break;
              ny++;
            }
            const key = `${nx},${ny}`;
            if (!visited.has(key)) {
              visited.add(key);
              queue.push({ x: nx, y: ny });
            }
          }
        }
        
        // Dig left/right
        for (const dx of [-1, 1]) {
          const nx = x + dx;
          const ny = y + 1;
          if (map.canDig(nx, ny)) {
            // After digging, we can reach the dug hole
            const key = `${nx},${ny}`;
            if (!visited.has(key)) {
              visited.add(key);
              queue.push({ x: nx, y: ny });
            }
          }
        }
      }
      
      // Climb up (must be on ladder)
      if (onLadder && y > 0 && !map.isSolid(x, y - 1)) {
        const key = `${x},${y - 1}`;
        if (!visited.has(key)) {
          visited.add(key);
          queue.push({ x, y: y - 1 });
        }
      }
      
      // Climb down
      if ((onLadder || map.isClimbable(x, y + 1)) && y < map.height - 1 && !map.isSolid(x, y + 1)) {
        const key = `${x},${y + 1}`;
        if (!visited.has(key)) {
          visited.add(key);
          queue.push({ x, y: y + 1 });
        }
      }
    }
    
    return visited;
  }
  
  private placeEnemies(map: TileMap): void {
    const [baseMin, baseMax] = this.difficulty.enemies;
    
    // Scale enemy count with level progression
    // Every 5 levels, add 1 to both min and max (capped)
    const levelBonus = Math.floor((this.levelNumber - 1) / 5);
    
    // Max enemies caps based on difficulty:
    // Easy: max 4, Normal: max 5, Hard: max 6, Ninja: max 7
    const maxCaps: { [key: string]: number } = {
      easy: 4,
      normal: 5,
      hard: 6,
      ninja: 7
    };
    const maxCap = maxCaps[this.difficultyKey] || 5;
    
    const minEnemies = Math.min(baseMin + levelBonus, maxCap - 1);
    const maxEnemies = Math.min(baseMax + levelBonus, maxCap);
    const enemyCount = this.rng.range(minEnemies, maxEnemies + 1);
    
    const validSpots: { x: number; y: number }[] = [];
    
    for (let y = 0; y < map.height - 1; y++) {
      for (let x = 0; x < map.width; x++) {
        if (this.isValidEnemySpot(map, x, y)) {
          // Must be far from player start
          const dist = Math.abs(x - map.playerStart.x) + Math.abs(y - map.playerStart.y);
          if (dist > 8) {
            validSpots.push({ x, y });
          }
        }
      }
    }
    
    this.rng.shuffle(validSpots);
    
    // Place enemies ensuring no duplicates and minimum spacing
    const placedPositions: Set<string> = new Set();
    for (const spot of validSpots) {
      if (map.enemyStarts.length >= enemyCount) break;
      
      const key = `${spot.x},${spot.y}`;
      if (placedPositions.has(key)) continue;
      
      // Ensure minimum spacing from other enemies (at least 3 tiles apart)
      const tooClose = map.enemyStarts.some(e => 
        Math.abs(e.x - spot.x) + Math.abs(e.y - spot.y) < 3
      );
      if (tooClose) continue;
      
      placedPositions.add(key);
      map.enemyStarts.push(spot);
    }
  }
  
  private isValidEnemySpot(map: TileMap, x: number, y: number): boolean {
    const tile = map.getTile(x, y);
    if (tile !== TileType.EMPTY && tile !== TileType.LADDER) return false;
    
    const below = map.getTile(x, y + 1);
    return below === TileType.BRICK || 
           below === TileType.BRICK_HARD || 
           below === TileType.LADDER;
  }
  
  private addExitLadders(map: TileMap): void {
    // In classic Lode Runner, the exit ladder is hidden until all gold is collected.
    // The exit ladder extends from the top of an existing ladder to row 0.
    // IMPORTANT: No ladder should visibly reach row 0-3 at level start.
    
    const EXIT_HIDDEN_ROWS = 4; // Rows 0-3 are always hidden initially
    
    // First, clear any ladders that might have been placed in the top rows
    // (they should all be hidden exit ladders)
    for (let y = 0; y < EXIT_HIDDEN_ROWS; y++) {
      for (let x = 0; x < map.width; x++) {
        if (map.getTile(x, y) === TileType.LADDER) {
          map.setTile(x, y, TileType.EMPTY);
        }
      }
    }
    
    // Find existing ladders that reach the upper area (row 4-6, just below hidden zone)
    // Only consider positions where the exit path (rows 0-3) is clear of poles/obstacles
    // AND the ladder extends far enough down to be well-connected
    const exitCandidates: { x: number; topY: number; ladderLength: number }[] = [];
    for (let x = 2; x < map.width - 2; x++) {
      // Check if exit path is clear (no poles or solid blocks in rows 0-3)
      let pathClear = true;
      for (let y = 0; y < EXIT_HIDDEN_ROWS; y++) {
        const tile = map.getTile(x, y);
        if (tile === TileType.POLE || tile === TileType.BRICK || 
            tile === TileType.BRICK_HARD || tile === TileType.BRICK_TRAP) {
          pathClear = false;
          break;
        }
      }
      if (!pathClear) continue;
      
      // Find the topmost ladder segment at this x and measure its length
      let topY = -1;
      let ladderLength = 0;
      for (let y = EXIT_HIDDEN_ROWS; y < map.height; y++) {
        if (map.getTile(x, y) === TileType.LADDER) {
          if (topY === -1) topY = y;
          ladderLength++;
        } else if (topY !== -1) {
          break; // End of contiguous ladder
        }
      }
      
      // Only consider ladders that start near the top and are long enough
      // to likely connect to the rest of the level
      if (topY !== -1 && topY <= EXIT_HIDDEN_ROWS + 2 && ladderLength >= 4) {
        exitCandidates.push({ x, topY, ladderLength });
      }
    }
    
    // Sort by ladder length (longer = better connected), then by topY
    exitCandidates.sort((a, b) => {
      if (b.ladderLength !== a.ladderLength) return b.ladderLength - a.ladderLength;
      return a.topY - b.topY;
    });
    
    let exitX: number;
    
    if (exitCandidates.length > 0) {
      // Pick from top candidates with randomness
      const candidateCount = Math.min(3, exitCandidates.length);
      const pickIdx = this.rng.range(0, candidateCount);
      exitX = exitCandidates[pickIdx].x;
    } else {
      // No suitable ladder found - find a clear column near center
      exitX = Math.floor(map.width / 2);
      
      // Search for a column that's clear of poles in the exit zone
      for (let dx = 0; dx < map.width / 2; dx++) {
        for (const offset of [dx, -dx]) {
          const testX = exitX + offset;
          if (testX < 2 || testX >= map.width - 2) continue;
          
          let clear = true;
          for (let y = 0; y < EXIT_HIDDEN_ROWS; y++) {
            const tile = map.getTile(testX, y);
            if (tile === TileType.POLE || tile === TileType.BRICK ||
                tile === TileType.BRICK_HARD || tile === TileType.BRICK_TRAP) {
              clear = false;
              break;
            }
          }
          
          if (clear) {
            exitX = testX;
            break;
          }
        }
      }
      
      // Clear any poles in the exit path (safety measure)
      for (let y = 0; y < EXIT_HIDDEN_ROWS; y++) {
        if (map.getTile(exitX, y) === TileType.POLE) {
          map.setTile(exitX, y, TileType.EMPTY);
        }
      }
      
      // Make sure there's a ladder leading up to the exit zone
      for (let y = EXIT_HIDDEN_ROWS; y < EXIT_HIDDEN_ROWS + 4; y++) {
        if (map.getTile(exitX, y) !== TileType.LADDER) {
          map.setTile(exitX, y, TileType.LADDER);
        }
      }
    }
    
    // Create hidden exit ladder from row 0 to row 3 (the hidden zone)
    for (let y = 0; y < EXIT_HIDDEN_ROWS; y++) {
      map.exitLadders.push({ x: exitX, y });
    }
  }
  
  private generateFallback(): TileMap {
    const map = new TileMap();
    
    // Ground
    for (let x = 0; x < map.width; x++) {
      map.setTile(x, map.height - 1, TileType.BRICK);
    }
    
    // Platform 1 (middle height)
    for (let x = 3; x < 18; x++) {
      map.setTile(x, 10, TileType.BRICK);
    }
    
    // Platform 2 (higher)
    for (let x = 10; x < 25; x++) {
      map.setTile(x, 6, TileType.BRICK);
    }
    
    // Ladders
    for (let y = 10; y < map.height - 1; y++) {
      map.setTile(8, y, TileType.LADDER);
    }
    for (let y = 6; y < 10; y++) {
      map.setTile(14, y, TileType.LADDER);
    }
    // Add visible ladder leading up to row 4 (NOT to top - exit is hidden)
    for (let y = 4; y < 6; y++) {
      map.setTile(20, y, TileType.LADDER);
    }
    // Hidden exit ladder from row 0-3 (revealed when all gold collected)
    for (let y = 0; y < 4; y++) {
      map.exitLadders.push({ x: 20, y });
    }
    
    // Poles
    for (let x = 18; x < 24; x++) {
      map.setTile(x, 10, TileType.POLE);
    }
    
    // Player start
    map.playerStart = { x: 3, y: map.height - 2 };
    
    // Gold
    const goldSpots = [
      { x: 5, y: 9 }, { x: 10, y: 9 }, { x: 15, y: 9 },
      { x: 12, y: 5 }, { x: 18, y: 5 }, { x: 22, y: 5 }
    ];
    for (const spot of goldSpots) {
      map.setTile(spot.x, spot.y, TileType.GOLD);
      map.goldPositions.push(spot);
    }
    
    // Enemies
    map.enemyStarts.push({ x: 22, y: map.height - 2 });
    map.enemyStarts.push({ x: 16, y: 9 });
    
    return map;
  }
}
